import { Subscribable, SubscribableOrNullableValue } from 'knockout';
import { withEffects } from 'mixins/withEffects';
import TasksQueueManager from './tasksQueue';

export interface ItemsPagerConfig<T> {
    items: SubscribableOrNullableValue<Array<T>>
    pinnedItems?: SubscribableOrNullableValue<Array<T>>
    pageSize?: SubscribableOrNullableValue<number>

    showAllItems?: SubscribableOrNullableValue<boolean>
}

export default class ItemsPager<T> {
    private effects = withEffects();
    private syncQueue = new TasksQueueManager();

    //amount of items we added to the beginning
    //allows to not recalculate pages boundaries
    private extraItemsToShow = ko.observable(0);

    private allItems: Subscribable<Array<T>>
    private pinnedItems: Subscribable<Array<T>>
    private pagedItems: Subscribable<Array<T>>

    private splitByIdx: Subscribable<number | undefined>;
    private itemsCountToShow: Subscribable<number>;

    private showAllItemsMode: Subscribable<boolean>

    afterSyncActions = ko.observableArray<Action>();
    items = ko.observableArray<T>()
    
    pagesCount: Subscribable<number>
    pagesCountToShow = ko.observable(1);

    newItems: Subscribable<Array<T>>

    constructor(config: ItemsPagerConfig<T>) {
        this.allItems = ko.flattenComputed(config.items, []);
        this.pinnedItems = ko.flattenComputed(config.pinnedItems, []).extend({ trackArrayChanges: true });
        this.showAllItemsMode = ko.flattenComputed(config.showAllItems, false);

        const pageSize = ko.flattenComputed(config.pageSize, 10);
        const firstItem = this.items.first();

        this.splitByIdx = ko.strictComputed((allItems, firstItem) => {
            if (firstItem != undefined)
                return allItems.indexOf(firstItem);
        }, [this.allItems, firstItem]);

        this.newItems = ko.pureComputed(() => this.getNewItems());        

        this.pagedItems = ko.strictComputed((allItems, splitIdx) => {
            if (splitIdx != undefined)
                return allItems.filter((item, idx) => idx >= splitIdx);

            return allItems;
        }, [this.allItems, this.splitByIdx]);

        this.pagesCount = ko.strictComputed((items, pageSize) => Math.ceil(items.length / pageSize), [this.pagedItems, pageSize]);

        this.itemsCountToShow = ko.strictComputed((pagesCount, pageSize, extraCommentsCount) => {
            return pagesCount * pageSize + extraCommentsCount;
        }, [this.pagesCountToShow, pageSize, this.extraItemsToShow]);

        this.effects.register(() => {
            this.sync(false);
        }, [this.pagesCountToShow]);

        this.effects.register([
            this.pinnedItems.onArrayChange({ add: () => this.sync(false), remove: () => this.sync(false) }),
            this.showAllItemsMode.subscribe(() => this.sync(false))
        ]);

        this.effects.register(showAllItemsMode => {
            if (showAllItemsMode)
                return this.allItems.subscribe(() => this.sync(false));
        }, [this.showAllItemsMode]);

        //this.effects.register([
        //    ko.computed(() => console.log('pagesCount', this.pagesCount())),
        //    ko.computed(() => console.log('pagesCountToShow', this.pagesCountToShow())),
        //    ko.computed(() => console.log('itemsCountToShow', this.itemsCountToShow())),
        //    ko.computed(() => console.log('extraItemsToShow', this.extraItemsToShow())),
        //    ko.computed(() => console.log('items', this.items()))
        //]);
    }

    async sync(grabNew = true) {
        this.syncQueue.run(() => new Promise<void>(resolve => {
            this.syncOrder();

            let pickedItems: Array<T>;

            if (this.showAllItemsMode()) {
                pickedItems = _.clone(this.allItems());
            } else {
                const pagedItems = this.pagedItems().slice(0, this.itemsCountToShow());

                if (grabNew) {
                    const newItems = this.newItems();
                    pickedItems = newItems.concat(pagedItems);

                    this.extraItemsToShow.inc(newItems.length);
                } else {
                    pickedItems = pagedItems;
                }
            }

            const pinnedItems = this.pinnedItems();

            if (pinnedItems.length > 0)
                pickedItems = _.uniq(pinnedItems.concat(pickedItems));

            this.items(pickedItems);

            this.afterSyncActions().forEach(action => action());
            this.afterSyncActions([]);

            resolve();
        }));        
    }

    showNextPage() {
        if (this.pagesCountToShow() < this.pagesCount())
            this.pagesCountToShow.inc();            
    }

    reset() {
        this.extraItemsToShow(0);
        this.pagesCountToShow(1);
        this.sync(false);
    }

    dispose() {
        this.effects.dispose();
    }

    private syncOrder() {
        const sortedItems = _(this.allItems()).intersection(this.items());

        this.items(sortedItems);
    }

    private getNewItems() {
        if (!this.showAllItemsMode()) {
            const splitIdx = this.splitByIdx();

            if (splitIdx != undefined)
                return this.allItems().filter((comment, idx) => idx < splitIdx);

            return [];
        }

        return _(this.allItems()).difference(this.items());
    }
}